import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
} from 'react';
import { ChevronDown, ChevronRight, Plus } from 'lucide-react';
import {
  Kanban,
  KanbanBoard,
  KanbanColumn,
  KanbanColumnContent,
  KanbanItem,
  KanbanItemHandle,
  KanbanOverlay,
  type KanbanCommitMeta,
} from '../reui/kanban';
import { cn } from '../../lib/utils';
import {
  workItemSubtypeLabel,
  workItemTypeLabel,
  type LabelDto,
  type ListResponse,
  type MemberDto,
  type WorkItemListDecorations,
  type WorkItemSlim,
} from '../../lib/work-types';
import { apiFetch, v1Path } from '../../lib/api';
import { doneToggleStateId } from '../../lib/done-state';
import { Checkbox } from '../ui/checkbox';
import { useOpenWorkItem } from './ControlLink';
import { useOverlays } from './overlay-context';
import { sortColumnItems, type KanbanItem as RankItem } from './kanban-rank';
import { KANBAN_BOARD_CLASSNAME, KANBAN_COLUMN_SHELL_CLASSNAME } from './work-kanban-layout';
import { BlockedBadge, CardPriority, WorkCardBadges, WorkCardLabels } from './WorkCardMeta';
import { useChangeMoment } from './use-moment';
import { useProjectDecorations } from './use-project-decorations';
import { ProgressMeter } from './ProgressParts';
import { PLAIN_STATUS_DOT, PLAIN_STATUS_LABEL, plainStatus, stepDetail } from '../../lib/plain-status';

export type WorkKanbanCard = RankItem & {
  title: string;
  priority: string;
  version: number;
  project_id?: string;
  parent_id?: string | null;
  assignee_ids?: string[];
  label_ids?: string[];
  start_date?: string | null;
  due_date?: string | null;
  type?: string;
  subtype?: string | null;
  blocked_reason?: string | null;
} & WorkItemListDecorations;

export type WorkKanbanColumnMeta = {
  id: string;
  name: string;
  color: string;
  group?: string;
};

type Props<T extends WorkKanbanCard> = {
  projectId?: string;
  columns: WorkKanbanColumnMeta[];
  items: T[];
  showEmptyGroups: boolean;
  canReorder: boolean;
  columnIdForItem?: (item: T) => string;
  onMove: (item: T, destStateId: string, insertBeforeId: string | null) => void;
  /** Trello-style select control, revealed on hover to the left of the title. */
  renderSelect?: (item: T) => ReactNode;
  /** Card actions, revealed on hover in the top-right corner. */
  renderActions?: (item: T) => ReactNode;
  /** Opens the shared create screen for that column. Omit to hide the footer. */
  onAddCard?: (stateId: string) => void;
  /** Replaces the standard card body, e.g. with a queue-specific face. */
  renderCardFace?: (args: { item: T; select: ReactNode; actions: ReactNode }) => ReactNode;
  /** Resolves stream-specific subtype names for cross-stream boards. */
  subtypeLabelForItem?: (item: T) => string | undefined;
};

const CARD_CLICK_SLOP_PX = 10;

function isInteractiveCardTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return true;
  const hit = target.closest(
    'a, button, input, textarea, select, [role="checkbox"], [role="menuitem"]',
  );
  if (!hit) return false;
  return hit.getAttribute('data-slot') !== 'kanban-item-handle';
}

function toBoard<T extends WorkKanbanCard>(
  columns: WorkKanbanColumnMeta[],
  items: T[],
  showEmptyGroups: boolean,
  columnIdForItem?: (item: T) => string,
): Record<string, T[]> {
  const grouped = new Map<string, T[]>();
  for (const item of items) {
    const columnId = columnIdForItem?.(item) ?? item.state_id;
    const list = grouped.get(columnId) ?? [];
    list.push(item);
    grouped.set(columnId, list);
  }
  const board: Record<string, T[]> = {};
  for (const column of columns) {
    const list = (grouped.get(column.id) ?? []).sort(sortColumnItems);
    if (!showEmptyGroups && list.length === 0) continue;
    board[column.id] = list;
  }
  return board;
}

function getCardId<T extends WorkKanbanCard>(item: T): string {
  return item.id;
}

/**
 * Card controls stay out of the way until the card is hovered, focused or selected.
 * Written out as literal class names so Tailwind's scanner can see every variant.
 */
const CARD_CONTROL = [
  'pointer-events-none opacity-0 transition-opacity duration-150 ease-out',
  'group-hover/card:pointer-events-auto group-hover/card:opacity-100',
  'group-focus-within/card:pointer-events-auto group-focus-within/card:opacity-100',
  'group-has-[[data-state=checked]]/card:pointer-events-auto',
  'group-has-[[data-state=checked]]/card:opacity-100',
].join(' ');

/** Makes room for the select circle as it fades in. */
const CARD_TITLE_INDENT = [
  'transition-[padding] duration-150 ease-out',
  'group-hover/card:pl-6 group-focus-within/card:pl-6',
  'group-has-[[data-state=checked]]/card:pl-6',
].join(' ');

function ColumnAddCard({ columnName, onAdd }: { columnName: string; onAdd: () => void }) {
  return (
    <button
      type="button"
      className="flex w-full items-center gap-1.5 rounded-[8px] px-2 py-1.5 text-left text-sm text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground"
      onClick={onAdd}
    >
      <Plus className="h-4 w-4 shrink-0" aria-hidden />
      Add a card
      <span className="sr-only"> to {columnName}</span>
    </button>
  );
}

function familyHint<T extends WorkKanbanCard>(item: T, items: T[]): string | null {
  if (item.parent_id) {
    const parent = items.find((row) => row.id === item.parent_id);
    return parent ? `Subtask of ${parent.key}` : 'Subtask';
  }
  if (item.rollup) return null;
  const childCount = items.filter((row) => row.parent_id === item.id).length;
  if (childCount === 0) return null;
  return childCount === 1 ? '1 subtask' : `${childCount} subtasks`;
}

const SETTLED_GROUPS = new Set(['completed', 'cancelled']);

/** Subtasks load on demand so a board of collapsed cards costs nothing extra. */
function CardSubtasks({
  projectId,
  parentId,
  count,
  stateColor,
  subtypes,
  columns,
}: {
  projectId: string;
  parentId: string;
  count: number;
  stateColor: (stateId: string) => { color?: string; group?: string };
  subtypes: ReturnType<typeof useProjectDecorations>['subtypes'];
  columns: WorkKanbanColumnMeta[];
}) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<WorkItemSlim[] | null>(null);
  const [error, setError] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const { toast } = useOverlays();

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError(false);
    void apiFetch<ListResponse<WorkItemSlim>>(
      `${v1Path(`/projects/${projectId}/work-items`)}?parent_ids=${parentId}&show_sub_items=true&limit=50&offset=0`,
    )
      .then((res) => {
        if (!cancelled) setRows(res.items);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open, parentId, projectId]);

  return (
    <div onPointerDown={(event) => event.stopPropagation()}>
      <button
        type="button"
        className="inline-flex items-center gap-1 rounded px-1 py-0.5 text-[11px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
        aria-expanded={open}
        onClick={() => setOpen((curr) => !curr)}
      >
        {open ? <ChevronDown className="h-3 w-3" aria-hidden /> : <ChevronRight className="h-3 w-3" aria-hidden />}
        {count === 1 ? '1 subtask' : `${count} subtasks`}
      </button>
      {open ? (
        <ul className="mt-1 space-y-0.5 border-l pl-2">
          {error ? <li className="text-[11px] text-destructive">Could not load subtasks.</li> : null}
          {!error && rows === null ? <li className="text-[11px] text-muted-foreground">Loading...</li> : null}
          {(rows ?? []).map((row) => {
            const state = stateColor(row.state_id);
            const settled = state.group ? SETTLED_GROUPS.has(state.group) : false;
            const done = state.group === 'completed';
            const dest = doneToggleStateId(columns, !done);
            return (
              <li key={row.id} className="flex items-center gap-1.5 text-[12px] leading-5">
                <Checkbox
                  checked={done}
                  disabled={busyId === row.id || !dest}
                  aria-label={done ? `Mark ${row.title} not done` : `Mark ${row.title} done`}
                  onClick={(event) => event.stopPropagation()}
                  onCheckedChange={() => {
                    if (!dest || busyId) return;
                    setBusyId(row.id);
                    void apiFetch<WorkItemSlim>(v1Path(`/projects/${projectId}/work-items/${row.id}/move`), {
                      method: 'POST',
                      body: JSON.stringify({ version: row.version, state_id: dest }),
                    })
                      .then((updated) => {
                        setRows((current) => current?.map((item) => (
                          item.id === row.id ? { ...item, state_id: updated.state_id, version: updated.version } : item
                        )) ?? current);
                        window.dispatchEvent(new CustomEvent('bb:work-items-changed'));
                      })
                      .catch((err: unknown) => {
                        toast(err instanceof Error ? err.message : 'Could not update the subtask.');
                      })
                      .finally(() => setBusyId(null));
                  }}
                />
                <span className={cn('min-w-0 flex-1 truncate', settled && 'text-muted-foreground line-through')}>
                  {row.title}
                </span>
                {row.subtype ? (
                  <span className="shrink-0 text-[10px] text-muted-foreground">
                    {workItemSubtypeLabel(row.subtype, subtypes)}
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

function KanbanCardFace<T extends WorkKanbanCard>({
  item,
  items,
  labels,
  members,
  subtypes,
  subtypeLabelForItem,
  stateColor,
  projectId,
  columns,
  select,
  actions,
}: {
  item: T;
  items: T[];
  stateColor: (stateId: string) => { color?: string; group?: string };
  projectId?: string;
  columns: WorkKanbanColumnMeta[];
  labels: LabelDto[];
  members: MemberDto[];
  subtypes: ReturnType<typeof useProjectDecorations>['subtypes'];
  subtypeLabelForItem?: (item: T) => string | undefined;
  select: ReactNode;
  actions: ReactNode;
}) {
  const hint = familyHint(item, items);
  const group = stateColor(item.state_id).group;
  const done = group === 'completed' || group === 'cancelled';
  const moment = useChangeMoment(
    `${group === 'completed' ? 'done' : 'open'}:${item.blocked_reason ? 'waiting' : 'free'}`,
    (was, now) => {
      if (now.startsWith('done') && !was.startsWith('done')) return 'done';
      if (was.endsWith('waiting') && now.endsWith('free')) return 'unblocked';
      return null;
    },
    950,
    `card:${item.id}`,
  );
  return (
    <div
      className={cn(
        'relative rounded-[12px] bg-card px-3.5 py-3 shadow-sm ring-1 ring-black/[0.06] transition-[box-shadow,transform] duration-150 ease-out hover:-translate-y-px hover:shadow-md hover:ring-black/10 motion-reduce:transition-none motion-reduce:hover:translate-y-0 dark:ring-white/10 dark:hover:ring-white/20',
        moment === 'done' && 'animate-done-card motion-reduce:animate-none',
        moment === 'unblocked' && 'animate-release-card motion-reduce:animate-none',
      )}
    >
      {actions ? (
        <div className={cn('absolute bottom-2 right-2 z-10', CARD_CONTROL)}>
          {actions}
        </div>
      ) : null}
      <div className="relative">
        {select ? (
          <div className={cn('absolute left-0 top-0.5', CARD_CONTROL)}>{select}</div>
        ) : null}
        <div className="flex items-start justify-between gap-2">
          <p
            className={cn(
              'min-w-0 text-sm font-semibold leading-5 text-foreground',
              select && CARD_TITLE_INDENT,
            )}
          >
            {item.title}
          </p>
          <CardPriority priority={item.priority} type={item.type} />
        </div>
        <p className={cn('mt-0.5 text-[11px] leading-4 text-muted-foreground', select && CARD_TITLE_INDENT)}>
          {item.key}
          {item.type && item.type !== 'task' ? ` · ${workItemTypeLabel(item.type)}` : ''}
          {item.subtype
            ? ` · ${subtypeLabelForItem?.(item) ?? workItemSubtypeLabel(item.subtype, subtypes)}`
            : ''}
          {hint ? ` · ${hint}` : ''}
        </p>
        <BlockedBadge reason={item.blocked_reason} className={cn('mt-1.5', select && CARD_TITLE_INDENT)} />
      </div>
      {item.rollup && item.rollup.total + item.rollup.cancelled > 0 ? (
        <div className="mt-2 space-y-1.5">
          {item.rollup.total > 0 ? <ProgressMeter counts={item.rollup} /> : null}
          {item.project_id ?? projectId ? (
            <CardSubtasks
              projectId={(item.project_id ?? projectId)!}
              parentId={item.id}
              count={item.rollup.total + item.rollup.cancelled}
              stateColor={stateColor}
              subtypes={subtypes}
              columns={columns}
            />
          ) : null}
        </div>
      ) : null}
      <div className={cn('mt-2.5 space-y-2', actions && 'pr-7')}>
        <WorkCardBadges item={item} members={members} done={done} />
        <WorkCardLabels item={item} labels={labels} />
      </div>
    </div>
  );
}

export default function WorkKanban<T extends WorkKanbanCard>({
  projectId,
  columns,
  items,
  showEmptyGroups,
  canReorder,
  columnIdForItem,
  onMove,
  renderSelect,
  renderActions,
  onAddCard,
  renderCardFace,
  subtypeLabelForItem,
}: Props<T>) {
  const source = useMemo(
    () => toBoard(columns, items, showEmptyGroups, columnIdForItem),
    [columns, items, showEmptyGroups, columnIdForItem],
  );
  const [board, setBoard] = useState(source);
  // Empty columns fold to a slim strip so the board fits; they stay drop targets.
  const [openEmpty, setOpenEmpty] = useState<Set<string>>(() => new Set());
  function toggleEmpty(columnId: string) {
    setOpenEmpty((current) => {
      const next = new Set(current);
      if (next.has(columnId)) next.delete(columnId);
      else next.add(columnId);
      return next;
    });
  }

  useEffect(() => {
    setBoard(source);
  }, [source]);

  const columnById = useMemo(() => {
    return new Map(columns.map((column) => [column.id, column]));
  }, [columns]);

  const handleCommit = useCallback(
    (next: Record<string, T[]>, meta: KanbanCommitMeta<T>) => {
      if (meta.kind !== 'item') {
        setBoard(meta.previousValue);
        return;
      }
      const dest = next[meta.overContainer];
      if (!dest) {
        setBoard(meta.previousValue);
        return;
      }
      const moved = dest[meta.overIndex];
      if (!moved) {
        setBoard(meta.previousValue);
        return;
      }
      const insertBeforeId = dest[meta.overIndex + 1]?.id ?? null;
      onMove(moved, meta.overContainer, insertBeforeId);
    },
    [onMove],
  );

  const { labels, members, subtypes } = useProjectDecorations(projectId);
  const openWorkItem = useOpenWorkItem();
  const stateColor = useCallback(
    (stateId: string) => {
      const column = columnById.get(stateId);
      return column ? { color: column.color, group: column.group } : {};
    },
    [columnById],
  );

  /** A custom face may decline an item (its extra data has not loaded yet); the standard face still renders it. */
  function cardFace(item: T, select: ReactNode, actions: ReactNode): ReactNode {
    return (
      renderCardFace?.({ item, select, actions })
      ?? (
        <KanbanCardFace
          item={item}
          items={items}
          labels={labels}
          members={members}
          subtypes={subtypes}
          subtypeLabelForItem={subtypeLabelForItem}
          stateColor={stateColor}
          projectId={projectId}
          columns={columns}
          select={select}
          actions={actions}
        />
      )
    );
  }

  const pointerOrigin = useRef<{ x: number; y: number; at: number } | null>(null);

  function onCardPointerDown(event: PointerEvent<HTMLDivElement>) {
    if (event.button !== 0) return;
    pointerOrigin.current = { x: event.clientX, y: event.clientY, at: Date.now() };
  }

  function openCardIfClick(
    event: Pick<MouseEvent<HTMLDivElement>, 'clientX' | 'clientY' | 'metaKey' | 'ctrlKey' | 'target'>,
    item: T,
  ) {
    const origin = pointerOrigin.current;
    pointerOrigin.current = null;
    if (origin && Date.now() - origin.at < 1000) {
      const dx = event.clientX - origin.x;
      const dy = event.clientY - origin.y;
      if (dx * dx + dy * dy > CARD_CLICK_SLOP_PX * CARD_CLICK_SLOP_PX) return;
    }
    if (isInteractiveCardTarget(event.target)) return;
    if (!item.id || !item.key) return;
    const cardProjectId = item.project_id ?? projectId;
    if (!cardProjectId) return;
    openWorkItem({
      workItemId: item.id,
      workItemKey: item.key,
      projectId: cardProjectId,
      metaKey: event.metaKey,
      ctrlKey: event.ctrlKey,
    });
  }

  return (
    <Kanban
      value={board}
      onValueChange={setBoard}
      getItemValue={getCardId}
      restoreOnCancel
      onValueCommit={handleCommit}
      className="flex flex-1 flex-col"
    >
      <KanbanBoard className={KANBAN_BOARD_CLASSNAME}>
        {Object.entries(board).map(([columnId, cards]) => {
          const column = columnById.get(columnId);
          if (!column) return null;
          const plain = column.group ? plainStatus({ group: column.group, name: column.name }) : null;
          const plainLabel = plain && stepDetail(plain, column.name) ? PLAIN_STATUS_LABEL[plain] : null;
          const folded = cards.length === 0 && !openEmpty.has(columnId);
          if (folded) {
            return (
              <KanbanColumn
                key={columnId}
                value={columnId}
                className="flex w-11 min-w-11 flex-none flex-col"
                aria-label={`${column.name} column, empty`}
              >
                <button
                  type="button"
                  title={`${column.name}: nothing here. Click to open.`}
                  onClick={() => toggleEmpty(columnId)}
                  className="flex flex-1 flex-col items-center gap-2 rounded-[14px] bg-muted/40 py-3 text-muted-foreground hover:bg-muted"
                >
                  <span
                    className={cn('h-2 w-2 shrink-0 rounded-full', plain ? PLAIN_STATUS_DOT[plain] : null)}
                    style={plain ? undefined : { backgroundColor: column.color }}
                    aria-hidden
                  />
                  <span className="text-xs font-medium [writing-mode:vertical-rl]">{column.name}</span>
                  <span className="text-[11px] tabular-nums">0</span>
                </button>
              </KanbanColumn>
            );
          }
          return (
            <KanbanColumn
              key={columnId}
              value={columnId}
              className="flex min-w-72 flex-1 flex-col"
              aria-label={`${column.name} column, ${cards.length} ${cards.length === 1 ? 'item' : 'items'}`}
            >
              <div className={KANBAN_COLUMN_SHELL_CLASSNAME}>
                <header className="flex items-center gap-2 px-2 pb-2 pt-1">
                  {cards.length === 0 ? (
                    <button
                      type="button"
                      title={`${column.name}: nothing here. Click to close.`}
                      onClick={() => toggleEmpty(columnId)}
                      className="flex min-w-0 flex-1 items-center gap-2 text-left"
                    >
                      <span
                        className={cn('h-2.5 w-2.5 shrink-0 rounded-full', plain ? PLAIN_STATUS_DOT[plain] : null)}
                        style={plain ? undefined : { backgroundColor: column.color }}
                        aria-hidden
                      />
                      <h2 className="text-sm font-semibold leading-5">{column.name}</h2>
                      <span className="text-xs tabular-nums text-muted-foreground">{cards.length}</span>
                    </button>
                  ) : (
                    <>
                      <span
                        className={cn('h-2.5 w-2.5 shrink-0 rounded-full', plain ? PLAIN_STATUS_DOT[plain] : null)}
                        style={plain ? undefined : { backgroundColor: column.color }}
                        aria-hidden
                      />
                      <h2 className="text-sm font-semibold leading-5">{column.name}</h2>
                      <span className="text-xs tabular-nums text-muted-foreground">{cards.length}</span>
                    </>
                  )}
                  {plainLabel ? (
                    <span className="ml-auto text-[11px] text-muted-foreground">{plainLabel}</span>
                  ) : null}
                </header>
                <KanbanColumnContent value={columnId} className="flex flex-1 flex-col gap-3 p-1">
                  {cards.length === 0 ? (
                    <button
                      type="button"
                      title={`${column.name}: nothing here. Click to close.`}
                      onClick={() => toggleEmpty(columnId)}
                      className="flex flex-1 flex-col items-center justify-center px-3 py-10 text-center"
                    >
                      <p className="text-sm font-medium text-muted-foreground">No items</p>
                      <p className="mt-1 text-xs text-muted-foreground/80">
                        Click to collapse.
                      </p>
                    </button>
                  ) : (
                    cards.map((item) => (
                      <KanbanItem
                        key={item.id}
                        value={item.id}
                        disabled={!canReorder}
                        onPointerDown={onCardPointerDown}
                        onClick={(event: MouseEvent<HTMLDivElement>) => openCardIfClick(event, item)}
                      >
                        <KanbanItemHandle
                          id={`issue-${item.id}`}
                          tabIndex={-1}
                          className="group/card"
                          aria-label={`${item.key} ${item.title}`}
                        >
                          {cardFace(item, renderSelect?.(item) ?? null, renderActions?.(item) ?? null)}
                        </KanbanItemHandle>
                      </KanbanItem>
                    ))
                  )}
                </KanbanColumnContent>
                {onAddCard ? (
                  <div className="mt-auto px-1 pt-1">
                    <ColumnAddCard
                      columnName={column.name}
                      onAdd={() => onAddCard(columnId)}
                    />
                  </div>
                ) : null}
              </div>
            </KanbanColumn>
          );
        })}
      </KanbanBoard>
      <KanbanOverlay className="origin-center rounded-[10px] shadow-lg motion-safe:scale-[1.015]">
        {({ value, variant }) => {
          if (variant !== 'item') return null;
          const item = Object.values(board)
            .flat()
            .find((row) => row.id === String(value));
          if (!item) return <div className="bg-muted size-full rounded-md" />;
          return <>{cardFace(item, null, null)}</>;
        }}
      </KanbanOverlay>
    </Kanban>
  );
}
